import { useAtomCallback } from "jotai/utils";
import qs from "qs";
import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { getAppConfig } from "../../config";
import { TrackingEvents, sendLoggingEvents } from "../../events";
import { useGetTelehealthSessionInfo, useIsZoomEnabled } from "../../hooks";
import { isRestartingSessionAtom } from "../state";
import { useVisitorDisconnectRoomCallback } from "./useVisitorDisconnectRoomCallback";
import { VideoPlatform } from "../../video-sdk";
import { telehealthSessionQualityFeedbackGate } from "../../statsig/gates";
import { Statsig } from "../../statsig/StatsigProvider";

export function useLeaveSessionCallback() {
  const { providerShortId, patientShortId } = useParams() as {
    providerShortId: string;
    patientShortId: string;
  };
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const disconnectRoom = useVisitorDisconnectRoomCallback();
  const getIsRestartingSession = useAtomCallback(
    useCallback((get) => get(isRestartingSessionAtom), []),
  );
  const [_, checkZoomIsEnabled] = useIsZoomEnabled();

  const callback = useCallback(
    function redirectToPostSession() {
      // Check if provider is restarting session
      if (getIsRestartingSession()) {
        return;
      }

      if (!data) return;
      const isSessionQualityFeedbackEnabled = Statsig.checkGate(
        telehealthSessionQualityFeedbackGate,
      );
      const videoPlatformParam = isSessionQualityFeedbackEnabled
        ? {
            video_platform: checkZoomIsEnabled()
              ? VideoPlatform.Zoom
              : VideoPlatform.Twilio,
          }
        : {};

      sendLoggingEvents(TrackingEvents.CLIENT_POST_SESSION_REDIRECT);
      const appointmentShortId =
        data.telehealthSessionInfo?.appointment?.shortId;
      const searchQueryParams = {
        prsid: providerShortId,
        pid: patientShortId,
        utm_source: "telehealth-rematching",
        ...videoPlatformParam,
        ...(appointmentShortId && { appointment_id: appointmentShortId }),
      };

      const postSessionLink = `${
        getAppConfig().marketplaceOrigin
      }/session-completed?${qs.stringify(searchQueryParams)}`;

      disconnectRoom(); // gracefully disconnect from the room
      window.location.href = postSessionLink;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, providerShortId, patientShortId, getIsRestartingSession],
  );

  return callback;
}
